import { createFileRoute } from "@tanstack/react-router";
import { useState, useRef, useEffect } from "react";
import { ContactList } from "@/components/inbox/contact-list";
import { MessageThread } from "@/components/inbox/message-thread";
import { ComposeArea } from "@/components/inbox/compose-area";
import type { ComposeAreaHandle } from "@/components/inbox/compose-area";
import { ContactDetails } from "@/components/inbox/contact-details";
import type { Contact, Message } from "@/components/inbox/types";
import {
  listContactThreadMessagesServer,
  fetchThreadsMessagesServer,
  sendMessageServer,
  fetchEmailAttachmentsServer,
} from "@/server/inbox";
import { getWorkspaceIdWithFallback } from "@/lib/routing/workspace-context";
import type { ListContactThreadMessagesResult } from "@/lib/api/generated";
import type { ListContactThreadMessagesApiWorkspacesWorkspaceIdInboxContactThreadsGetParams } from "@/lib/api/generated";
import type { ThreadIdentifier } from "@/lib/api/generated";
import { DealStage } from "@/lib/api/generated/dealStage";
import type { InboxContactItemSchema } from "@/lib/api/generated";
import { Skeleton } from "@/components/ui/skeleton";
import { getIntegrationAccountCountsServer } from "@/server/user-integrations";
import type { UserIntegrationCountsResponse } from "@/lib/api/generated";
import { toast } from "sonner";
import { NoData } from "@/components/ui/no-data";
import { t } from "i18next";

const MAX_THREADS_PER_FETCH = 100;

interface InboxSearchParams {
  threadId?: string;
}

export const Route = createFileRoute("/workspace/$workspaceSlug/inbox")({
  validateSearch: (search: Record<string, unknown>): InboxSearchParams => ({
    threadId:
      typeof search.threadId === "string" && search.threadId.trim().length > 0
        ? search.threadId.trim()
        : undefined,
  }),
  loader: async ({ context, parentMatchPromise }) => {
    const workspaceId = await getWorkspaceIdWithFallback(context, "inbox", parentMatchPromise);
    const res = (await listContactThreadMessagesServer({
      data: { workspaceId, params: { page: 1, page_size: 50 } },
    })) as ListContactThreadMessagesResult;

    let channelCounts: Record<"email" | "linkedin" | "whatsapp" | "sms", number> = {
      email: 0,
      linkedin: 0,
      whatsapp: 0,
      sms: 0,
    };
    try {
      const countsRes = (await getIntegrationAccountCountsServer({
        data: { workspaceId },
      })) as UserIntegrationCountsResponse;
      const m = countsRes.counts || {};
      const emailSum = (m["gmail"] || 0) + (m["microsoft_mail"] || 0) + (m["workus_email"] || 0);
      const whatsappSum = (m["whatsapp"] || 0) + (m["whatsapp_business"] || 0);
      channelCounts = {
        email: emailSum,
        linkedin: m["linkedin"] || 0,
        whatsapp: whatsappSum,
        sms: m["sms"] || 0,
      };
    } catch {
      channelCounts = { email: 0, linkedin: 0, whatsapp: 0, sms: 0 };
    }

    const contacts: Contact[] = (res.items ?? []).map((item) => {
      const firstName = item.contact.first_name ?? "";
      const lastName = item.contact.last_name ?? "";
      const name = `${firstName} ${lastName}`.trim() || item.contact.id;
      const email = item.contact.email ?? "";
      const role = item.contact.title ?? "";
      const company = item.company?.name ?? "";
      const avatar = item.company?.logo_url ?? "";
      const initials =
        `${firstName?.[0] ?? ""}${lastName?.[0] ?? ""}`.toUpperCase() ||
        (name ? name[0].toUpperCase() : "");

      let latestText = "";
      let latestDate = "";
      let hasInbound = false;
      const channels = new Set<string>();
      for (const deal of item.deals ?? []) {
        for (const thread of deal.threads ?? []) {
          hasInbound = hasInbound || !!thread.have_inbound_msgs;
          if (thread.thread_type) channels.add(thread.thread_type);
          const lm = thread.last_message;
          const lmAt = thread.last_message_at ?? lm?.created_at ?? "";
          if (!latestDate || (lmAt && new Date(lmAt).getTime() > new Date(latestDate).getTime())) {
            latestDate = lmAt;
            latestText = lm?.body_text ?? latestText;
          }
        }
      }

      return {
        id: item.contact.id,
        name,
        email,
        role,
        company,
        country: item.company?.country || "-",
        avatar,
        initials,
        lastMessage: latestText,
        date: latestDate
          ? new Date(latestDate).toLocaleDateString("en-US", { month: "short", day: "2-digit" })
          : "",
        hasAttachments: false,
        unread: hasInbound || undefined,
        channels: Array.from(channels) as ("email" | "whatsapp" | "linkedin" | "sms")[],
      } as Contact;
    });

    return {
      contacts,
      rawItems: res.items as InboxContactItemSchema[],
      channelCounts,
      workspaceId,
    };
  },
  component: InboxPage,
});

async function fetchMessagesInChunks(workspaceId: string, threads: ThreadIdentifier[]) {
  const chunkedThreads: ThreadIdentifier[][] = [];

  for (let index = 0; index < threads.length; index += MAX_THREADS_PER_FETCH) {
    chunkedThreads.push(threads.slice(index, index + MAX_THREADS_PER_FETCH));
  }

  const results = await Promise.all(
    chunkedThreads.map((chunk) =>
      fetchThreadsMessagesServer({
        data: {
          workspaceId,
          request: { threads: chunk },
        },
      }),
    ),
  );

  return results
    .flatMap((result) => result.messages)
    .sort(
      (left, right) => new Date(left.created_at).getTime() - new Date(right.created_at).getTime(),
    );
}

function findContactByThreadId(
  items: InboxContactItemSchema[],
  contacts: Contact[],
  threadId: string | undefined,
): Contact | null {
  if (!threadId) {
    return null;
  }

  const matchedItem = items.find((item) =>
    (item.deals ?? []).some((deal) =>
      (deal.threads ?? []).some((thread) => thread.thread_id === threadId),
    ),
  );

  if (!matchedItem) {
    return null;
  }

  return contacts.find((contact) => contact.id === matchedItem.contact.id) ?? null;
}

function InboxPage() {
  const { contacts, rawItems, channelCounts, workspaceId } = Route.useLoaderData() as {
    contacts: Contact[];
    rawItems: InboxContactItemSchema[];
    channelCounts: Record<"email" | "linkedin" | "whatsapp" | "sms", number>;
    workspaceId: string;
  };
  const { threadId } = Route.useSearch();
  const [contactList, setContactList] = useState<Contact[]>(contacts);
  const [contactItems, setContactItems] = useState<InboxContactItemSchema[]>(rawItems);
  const [selectedContact, setSelectedContact] = useState<Contact | null>(
    () => findContactByThreadId(rawItems, contacts, threadId) ?? contacts[0] ?? null,
  );
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [subjectText, setSubjectText] = useState("");
  const composeFormRef = useRef<ComposeAreaHandle>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const selectedItem = contactItems.find((i) => i.contact.id === selectedContact?.id);
  const [stageFilter, setStageFilter] = useState<keyof typeof DealStage | null>(null);
  const [loading, setLoading] = useState(false);
  const [messagesLoading, setMessagesLoading] = useState(false);

  useEffect(() => {
    const matchedContact = findContactByThreadId(contactItems, contactList, threadId);

    if (matchedContact) {
      if (matchedContact.id !== selectedContact?.id) {
        setSelectedContact(matchedContact);
      }
      return;
    }

    if (!selectedContact && contactList.length > 0) {
      setSelectedContact(contactList[0] ?? null);
    }
  }, [contactItems, contactList, selectedContact, threadId]);

  useEffect(() => {
    let cancelled = false;
    async function run() {
      setMessagesLoading(true);
      const contact = selectedContact;
      if (!contact) {
        if (!cancelled) {
          setMessages([]);
          setMessagesLoading(false);
        }
        return;
      }
      const item = contactItems.find((i) => i.contact.id === contact.id);
      if (!item) {
        if (!cancelled) {
          setMessages([]);
          setMessagesLoading(false);
        }
        return;
      }
      const threads = (item.deals ?? [])
        .flatMap((d) => d.threads ?? [])
        .map((t) => ({ thread_id: t.thread_id, thread_type: t.thread_type }));
      if (threads.length === 0) {
        if (!cancelled) {
          setMessages([]);
          setMessagesLoading(false);
        }
        return;
      }
      const fetchedMessages = await fetchMessagesInChunks(workspaceId, threads);
      if (cancelled) return;
      const formatted = fetchedMessages.map((m) => {
        const attachments: string[] | undefined =
          Array.isArray(m.attachments) && m.attachments.length > 0
            ? (m.attachments as string[])
            : undefined;

        return {
          id: m.id,
          threadId: m.thread_id,
          from: m.sender_display_name,
          to: [contact.email],
          cc: m.cc,
          subject: m.subject ?? "",
          content: m.body_html && m.body_html.length > 0 ? m.body_html : m.body_text,
          timestamp: new Date(m.created_at).toLocaleString("en-US", {
            year: "numeric",
            month: "long",
            day: "numeric",
            hour: "numeric",
            minute: "2-digit",
          }),
          status: m.direction === "inbound" ? "received" : "sent",
          channel: m.thread_type === "whatsapp" ? "whatsapp" : "email",
          attachments,
        };
      });
      setMessages(formatted as Message[]);
      setMessagesLoading(false);
    }
    void run();
    return () => {
      cancelled = true;
    };
  }, [selectedContact, contactItems, workspaceId]);

  async function handleAttachmentClick(message: Message, attachmentName: string) {
    try {
      const threadId = message.threadId;
      if (!threadId) {
        toast.error("Missing thread id");
        return;
      }

      const list = await fetchEmailAttachmentsServer({
        data: { workspaceId, threadId, messageId: message.id },
      });
      if (!Array.isArray(list) || list.length === 0) {
        toast.error("No attachments found");
        return;
      }

      const target = list.find((a) => a.filename === attachmentName) ?? list[0];

      if (!target || !target.download_url) {
        toast.error("Attachment URL not available");
        return;
      }

      if (typeof window !== "undefined") {
        const response = await fetch(target.download_url);
        if (!response.ok) {
          throw new Error(`download-fetch-failed:${response.status}`);
        }

        const blob = await response.blob();
        const objectUrl = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = objectUrl;
        link.download = target.filename || attachmentName || "attachment";
        link.rel = "noopener noreferrer";
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.setTimeout(() => {
          URL.revokeObjectURL(objectUrl);
        }, 1000);
      }
    } catch {
      toast.error("Failed to open attachment");
    }
  }

  function buildContacts(items: InboxContactItemSchema[]): Contact[] {
    return items.map((item) => {
      const firstName = item.contact.first_name ?? "";
      const lastName = item.contact.last_name ?? "";
      const name = `${firstName} ${lastName}`.trim() || item.contact.id;
      const email = item.contact.email ?? "";
      const role = item.contact.title ?? "";
      const company = item.company?.name ?? "";
      const avatar = item.company?.logo_url ?? "";
      const initials =
        `${firstName?.[0] ?? ""}${lastName?.[0] ?? ""}`.toUpperCase() ||
        (name ? name[0].toUpperCase() : "");

      let latestText = "";
      let latestDate = "";
      let hasInbound = false;
      const channels = new Set<string>();
      for (const deal of item.deals ?? []) {
        for (const thread of deal.threads ?? []) {
          hasInbound = hasInbound || !!thread.have_inbound_msgs;
          if (thread.thread_type) channels.add(thread.thread_type);
          const lm = thread.last_message;
          const lmAt = thread.last_message_at ?? lm?.created_at ?? "";
          if (!latestDate || (lmAt && new Date(lmAt).getTime() > new Date(latestDate).getTime())) {
            latestDate = lmAt;
            latestText = lm?.body_text ?? latestText;
          }
        }
      }

      return {
        id: item.contact.id,
        name,
        email,
        role,
        company,
        country: "",
        avatar,
        initials,
        lastMessage: latestText,
        date: latestDate
          ? new Date(latestDate).toLocaleDateString("en-US", { month: "short", day: "2-digit" })
          : "",
        hasAttachments: false,
        unread: hasInbound || undefined,
        channels: Array.from(channels) as ("email" | "whatsapp" | "linkedin" | "sms")[],
      } as Contact;
    });
  }

  async function handleSearch(opts: { stageLabel?: string; searchText?: string }) {
    setLoading(true);
    const key = (opts.stageLabel || "").trim().toLowerCase();
    const nextStageFilter =
      key && key !== "all" ? (key as keyof typeof DealStage) : key === "all" ? null : stageFilter;
    setStageFilter(nextStageFilter ?? null);
    const include_stage =
      nextStageFilter && DealStage[nextStageFilter as keyof typeof DealStage]
        ? DealStage[nextStageFilter]
        : null;
    const trimmed = (opts.searchText || "").trim();
    const params: ListContactThreadMessagesApiWorkspacesWorkspaceIdInboxContactThreadsGetParams = {
      page: 1,
      page_size: 50,
      include_stage,
      contact_name: trimmed || undefined,
      company_name: trimmed || undefined,
    };
    const res = (await listContactThreadMessagesServer({
      data: { workspaceId, params },
    })) as ListContactThreadMessagesResult;

    const nextItems = (res.items ?? []) as InboxContactItemSchema[];
    const nextContacts = buildContacts(nextItems);

    setContactItems(nextItems);
    setContactList(nextContacts);

    setSelectedContact(
      findContactByThreadId(nextItems, nextContacts, threadId) ?? nextContacts[0] ?? null,
    );
    setLoading(false);
  }

  return (
    <div className="relative flex h-svh w-full overflow-hidden bg-white">
      {/* Left Sidebar - Contact List */}
      <ContactList
        contacts={contactList}
        selectedContact={selectedContact}
        onSelectContact={setSelectedContact}
        onStatusChange={(label) => {
          void handleSearch({ stageLabel: label });
        }}
        onSearch={(value) => {
          void handleSearch({ searchText: value });
        }}
        loading={loading}
      />

      {/* Main Content - Messages & Compose */}
      <div className="flex-1 flex flex-col min-w-[450px] overflow-hidden">
        {selectedContact ? (
          messagesLoading ? (
            <InboxMessageThreadSkeleton />
          ) : (
            <MessageThread
              contact={selectedContact}
              messages={messages}
              detailsPanel={
                detailsOpen && selectedItem ? (
                  <ContactDetails item={selectedItem} workspaceId={workspaceId} />
                ) : null
              }
              onDetailsOpen={() => setDetailsOpen((open) => !open)}
              onReply={(subj) => {
                setSubjectText(subj);
                composeFormRef.current?.setSubjectInputVisible(false);
                composeFormRef.current?.activate();
              }}
              onAttachmentClick={(message, name) => {
                void handleAttachmentClick(message, name);
              }}
              footer={
                <ComposeArea
                  ref={composeFormRef}
                  subjectText={subjectText}
                  setSubjectText={setSubjectText}
                  accountCounts={channelCounts}
                  onSend={async ({
                    subject,
                    body_text,
                    body_html,
                    cc,
                    bcc,
                    channel,
                    attachments,
                  }) => {
                    const item = selectedItem;
                    if (!item) return;
                    // Pick latest thread of requested channel
                    const threads = (item.deals ?? [])
                      .flatMap((d) => d.threads ?? [])
                      .filter((t) => t.thread_type === channel);
                    const pick =
                      threads.sort((a, b) => {
                        const aAt = a.last_message_at || a.last_message?.created_at || "";
                        const bAt = b.last_message_at || b.last_message?.created_at || "";
                        return new Date(bAt).getTime() - new Date(aAt).getTime();
                      })[0] || threads[0];
                    if (!pick) {
                      toast.error("No thread found");
                      return;
                    }
                    const isEmail = channel === "email";
                    try {
                      await sendMessageServer({
                        data: {
                          workspaceId,
                          request: {
                            thread_id: pick.thread_id,
                            thread_type: pick.thread_type,
                            subject,
                            body_html: isEmail ? body_html : undefined,
                            body_text: isEmail ? "" : body_text,
                            cc,
                            bcc,
                            attachments: attachments?.map((a) => ({
                              filename: a.filename,
                              content: a.content,
                            })),
                          },
                        },
                      });
                    } catch (error) {
                      const message =
                        error instanceof Error && error.message
                          ? error.message
                          : "Failed to send message";
                      toast.error(message);
                      return;
                    }
                    // Reload messages after sending
                    setMessagesLoading(true);
                    const reloadThreads = (item.deals ?? [])
                      .flatMap((d) => d.threads ?? [])
                      .map((t) => ({ thread_id: t.thread_id, thread_type: t.thread_type }));
                    const fetchedMessages = await fetchMessagesInChunks(workspaceId, reloadThreads);
                    const formatted = fetchedMessages.map((m) => {
                      const attachments: string[] | undefined =
                        Array.isArray(m.attachments) && m.attachments.length > 0
                          ? (m.attachments as string[])
                          : undefined;

                      return {
                        id: m.id,
                        threadId: m.thread_id,
                        from: m.sender_display_name,
                        to: [selectedContact.email],
                        cc: m.cc,
                        subject: m.subject ?? "",
                        content: m.body_html && m.body_html.length > 0 ? m.body_html : m.body_text,
                        timestamp: new Date(m.created_at).toLocaleString("en-US", {
                          year: "numeric",
                          month: "long",
                          day: "numeric",
                          hour: "numeric",
                          minute: "2-digit",
                        }),
                        status: m.direction === "inbound" ? "received" : "sent",
                        channel: m.thread_type === "whatsapp" ? "whatsapp" : "email",
                        attachments,
                      };
                    });
                    setMessages(formatted as Message[]);
                    setMessagesLoading(false);
                  }}
                  contact={selectedContact}
                />
              }
            />
          )
        ) : (
          <NoData
            title={t("pages:inbox.empty.title")}
            description={t("pages:inbox.empty.description")}
            className="h-full w-full"
            iconSize={80}
          />
        )}
      </div>
    </div>
  );
}

function InboxMessageThreadSkeleton() {
  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex h-[65px] shrink-0 items-center justify-between border-b border-border px-6">
        <div className="flex min-w-0 items-center gap-3">
          <Skeleton className="size-10 shrink-0 rounded-full" />
          <div className="min-w-0 space-y-2">
            <Skeleton className="h-4 w-40 rounded-md" />
            <Skeleton className="h-3 w-56 rounded-md" />
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Skeleton className="h-8 w-20 rounded-full" />
          <Skeleton className="h-8 w-24 rounded-full" />
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-hidden px-8 py-5">
        <div className="mx-auto w-[95%] space-y-6">
          <MessageBubbleSkeleton align="left" lines={3} widthClass="w-[64%]" />
          <MessageBubbleSkeleton align="right" lines={2} widthClass="w-[58%]" />
          <MessageBubbleSkeleton align="left" lines={4} widthClass="w-[70%]" />
        </div>
      </div>

      <div className="shrink-0 border-t border-border px-6 py-4">
        <div className="rounded-[16px] border border-border bg-white p-4">
          <div className="mb-3 flex items-center gap-2">
            <Skeleton className="h-7 w-16 rounded-full" />
            <Skeleton className="h-7 w-20 rounded-full" />
          </div>
          <Skeleton className="h-4 w-1/2 rounded-md" />
          <Skeleton className="mt-3 h-20 w-full rounded-xl" />
          <div className="mt-3 flex justify-end">
            <Skeleton className="h-9 w-24 rounded-md" />
          </div>
        </div>
      </div>
    </div>
  );
}

function MessageBubbleSkeleton({
  align,
  lines,
  widthClass,
}: {
  align: "left" | "right";
  lines: number;
  widthClass: string;
}) {
  const isRight = align === "right";

  return (
    <div className={`flex ${isRight ? "justify-end" : "justify-start"}`}>
      <div className={`${widthClass} space-y-2`}>
        <div className={`flex items-center gap-2 ${isRight ? "justify-end" : "justify-start"}`}>
          {!isRight ? <Skeleton className="size-8 rounded-full" /> : null}
          <Skeleton className="h-3 w-28 rounded-md" />
          {isRight ? <Skeleton className="size-8 rounded-full" /> : null}
        </div>
        <div className="rounded-[12px] bg-[#F8F8F8] p-4">
          <Skeleton className="mb-3 h-6 w-32 rounded-lg" />
          {Array.from({ length: lines }).map((_, index) => (
            <Skeleton
              key={index}
              className={`mt-2 h-4 rounded-md ${index === lines - 1 ? "w-2/3" : "w-full"}`}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
